import type {
  FilterBarProps,
  FilterChangeEvent,
  FilterItem,
  FilterOptionSelect,
} from '@ctw/shared/components/filter-bar/filter-bar-types';
import type { LegacyMinRecordItem } from '@ctw/shared/components/tables/legacy-table';
import { ViewControlsToolbar } from '@ctw/shared/components/view-controls/view-controls-toolbar';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { Sort } from '@ctw/shared/utils/sort';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import { isEqual } from 'lodash-es';
import { type ReactNode, useState } from 'react';
import type { ViewOptions } from './view-option';

export type ResourceTableSortOption<T extends object> = {
  display: string;
  sorts: NonEmptyArray<Sort<T>>;
};

type ResourceTableSortOptions<T extends object> = {
  defaultSort: ResourceTableSortOption<T>;
  onChange: (sort: ResourceTableSortOption<T>) => void;
  options: Array<ResourceTableSortOption<T>>;
};

export type ResourceTableActionsProps<T extends LegacyMinRecordItem> = {
  headAction?: ReactNode;
  action?: ReactNode;
  className?: ClassName;
  filterOptions?: FilterBarProps;
  sortOptions?: ResourceTableSortOptions<T>;
  viewOptions?: ViewOptions<T>;
};

export const ResourceTableActions = <T extends LegacyMinRecordItem>({
  action,
  className,
  filterOptions,
  headAction,
  sortOptions,
  viewOptions,
}: ResourceTableActionsProps<T>) => {
  const { trackInteraction } = useTelemetry();
  const [currentRangeView, setCurrentRangeView] = useState<string | undefined>(
    viewOptions?.defaultView,
  );

  const isEmpty = !(viewOptions || sortOptions || filterOptions || action);
  if (isEmpty) {
    return null;
  }

  const rangeFilter: FilterOptionSelect | undefined = viewOptions
    ? {
        key: 'range',
        display: 'Range',
        type: 'select',
        promoted: true,
        values: viewOptions.options.map((option) => ({
          key: option.display,
          name: option.display,
        })),
      }
    : undefined;

  const toolbarFilters: Array<FilterItem> = [
    ...(rangeFilter ? [rangeFilter] : []),
    ...(filterOptions?.filters ?? []),
  ];

  const selectedWithRange: FilterChangeEvent = {
    ...(filterOptions?.selected ?? {}),
    ...(rangeFilter && currentRangeView
      ? { range: { key: 'range', type: 'select' as const, selected: currentRangeView } }
      : {}),
  };

  // The new UX batches filter changes (they only commit on the modal's "Update" button), so we
  // can't fire telemetry at the point of interaction the way the legacy FilterBar did. Instead,
  // diff the previously committed menu selections against the newly committed ones here.
  const fireFilterTelemetry = (oldSelected: FilterChangeEvent, newSelected: FilterChangeEvent) => {
    const oldKeys = Object.keys(oldSelected);
    const newKeys = Object.keys(newSelected);

    if (newKeys.length === 0 && oldKeys.length > 0) {
      trackInteraction('filter_clear_all');
      return;
    }

    for (const key of newKeys) {
      // A key absent from the previous selection is a newly added filter dimension, not a value
      // change on an existing one — classify it as `filter_add` to match the legacy FilterBar.
      if (oldSelected[key] === undefined) {
        trackInteraction('filter_add', {
          filter: key,
          filtersActive: newKeys,
        });
        continue;
      }

      if (!isEqual(oldSelected[key], newSelected[key])) {
        trackInteraction('filter_change', {
          filter: key,
          filterValue: '',
          filterValueChangeType: 'replace',
          filterValuesActive: newSelected[key]?.selected,
        });
      }
    }

    for (const key of oldKeys) {
      if (!(key in newSelected)) {
        trackInteraction('filter_remove', { filter: key, filtersActive: newKeys });
      }
    }
  };

  const handleToolbarFilterChange = (next: FilterChangeEvent) => {
    const rangeSelection = next.range;
    if (
      rangeFilter &&
      viewOptions &&
      rangeSelection?.type === 'select' &&
      rangeSelection.selected !== currentRangeView
    ) {
      const selectedView = viewOptions.options.find(
        (option) => option.display === rangeSelection.selected,
      );
      if (selectedView) {
        setCurrentRangeView(rangeSelection.selected);
        viewOptions.onChange(selectedView);
      }
    }
    const { range: _range, ...rest } = next;
    fireFilterTelemetry(filterOptions?.selected ?? {}, rest);
    filterOptions?.onChange(rest);
  };

  return (
    <div className={twx(className, 'flex flex-wrap')}>
      <div className={tw`flex w-full flex-wrap items-center gap-2`}>
        {headAction && <div className={tw`whitespace-nowrap`}>{headAction}</div>}
        <ViewControlsToolbar
          filters={toolbarFilters}
          selected={selectedWithRange}
          onFilterChange={handleToolbarFilterChange}
          sort={
            sortOptions
              ? {
                  defaultSort: sortOptions.defaultSort.display,
                  options: sortOptions.options.map((option) => option.display),
                  onChange: (display) => {
                    const selectedOption = sortOptions.options.find(
                      (option) => option.display === display,
                    );
                    if (selectedOption) {
                      trackInteraction('sort_change', {
                        sort: selectedOption.display,
                        sortDirection: selectedOption.sorts[0].dir,
                      });
                      sortOptions.onChange(selectedOption);
                    }
                  },
                }
              : undefined
          }
        />
      </div>
      <div className={tw`whitespace-nowrap`}>{action}</div>
    </div>
  );
};
